<template>
	<div id="set-dance-layout">
		<div id="dance-filters">
			<div id="dance-filters-group-one">
				<dance-filter
					v-for="filter in getDanceFiltersGroup('1')"
					:key="filter.dance"
					:dance-filter="filter"
					:active-filter="filterToApply"
					@dance-filter-clicked="changeFilter">
				</dance-filter>
			</div>
			<div id="dance-filters-group-two">
				<dance-filter
					v-for="filter in getDanceFiltersGroup('2')"
					:key="filter.dance"
					:dance-filter="filter"
					:active-filter="filterToApply"
					@dance-filter-clicked="changeFilter">
				</dance-filter>
			</div>
			<div id="dance-filters-group-three">
				<dance-filter
					v-for="filter in getDanceFiltersGroup('3')"
					:key="filter.dance"
					:dance-filter="filter"
					:active-filter="filterToApply"
					@dance-filter-clicked="changeFilter">
				</dance-filter>
			</div>
		</div>
		<div id="track-panels">
			<div id="track-panel-group-1">
				<div v-if="audioToPlayFirstGroup?.length > 0">
					<track-list 
						:audioFiles="audioToPlayFirstGroup" 
						v-on:track-selected="trackSelected"
						v-on:pause-selected="pauseAudio"></track-list>
				</div>
			</div>
			<div id="track-panel-group-2">
				<div id="second-set-dance-panel" v-if="audioToPlaySecondGroup?.length > 0">
					<track-list 
						:audioFiles="audioToPlaySecondGroup" 
						v-on:track-selected="trackSelected"
						v-on:pause-selected="pauseAudio"></track-list>
				</div>
			</div>
			<div id="track-panel-group-3">
				<div id="loop-panel" v-if="audioToPlayThirdGroup?.length > 0">
					<track-list 
						:panelHeader="'Drills'"
						:audioFiles="audioToPlayThirdGroup" 
						v-on:track-selected="trackSelected"
						v-on:pause-selected="pauseAudio"></track-list>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
// eslint-disable-next-line no-unused-vars
import SelectedDance from "@/model/selected_dance";
import DanceFilter from "@/model/dance_filter";
import * as setDanceFilterListing from "../../public/listing/set_dance_filters.json";
// eslint-disable-next-line no-unused-vars
import IConfigurationService from "../interfaces/configuration_service";
// eslint-disable-next-line no-unused-vars
import { defineComponent, onBeforeMount, PropType, reactive, ref, toRefs, watch } from "vue";

interface ISetDanceFilters {
	type: string,
	dance: string,
	group: string,
	isSelected: string
}
interface ISetDances {
	default: Array<ISetDanceFilters>
}

interface ISetDanceTracks {
		grade: string,
		category: string,
		group: string,
		trackListing: string,
		audioElement: string,
		isPlaying: boolean,
		isSelected: boolean
	}
interface ITracks {
	default: Array<ISetDanceTracks>
}

export default defineComponent({
  name: 'SetDances',
  emits: ["track-selected", "set-dance-name-clicked", "pause-selected"],
  props:
  {
	setDanceCategory: String,
	setDanceFilterToApply: String,
	audioToPlayFirstGroup:  Object as PropType<Array<SelectedDance>>,
	audioToPlaySecondGroup: Object as PropType<Array<SelectedDance>>,
	audioToPlayThirdGroup: Object as PropType<Array<SelectedDance>>
  },
  setup(props, { emit })
  {
	let filterToApply = ref<string | null>(null);
	let danceFilterToApply = ref<string | null>(null);
	let hornpipeSetDanceFilters = reactive<Array<DanceFilter>>([]);
	let jigSetDanceFilters = reactive<Array<DanceFilter>>([]);
	const allSetDanceFilters: ISetDances = JSON.parse(JSON.stringify(setDanceFilterListing));

	// fired when a filter is clicked and handles the data coming from grade_filter.vue component
	function changeFilter(value: string): void 
	{
		switch(props.setDanceCategory)
		{
			case "Hornpipe Set Dances":
				highlightSelectedHornpipeSetDanceName(value);
				filterToApply.value = props.setDanceCategory;
				break;
			case "Jig Set Dances":
				highlightSelectedJigSetDanceName(value);
				filterToApply.value = props.setDanceCategory;
				break;
			default:
				highlightSelectedHornpipeSetDanceName(value);
				filterToApply.value = "Hornpipe Set Dances";
				break;
		}

		if (filterToApply.value)
		{
			emit("set-dance-name-clicked", filterToApply.value, value)
		}
	}

	function setFilter()
	{
		if (props.setDanceCategory === 'Jig Set Dances')
		{
			changeFilter('Blackthorn Stick');
		}
		else
		{
			changeFilter('Ace and Deuce');
		}
	}
	
	watch(() => props.setDanceCategory, setFilter);

	function trackSelected(track: SelectedDance):void
	{
		emit("track-selected", track);
	}

	function pauseAudio(track: SelectedDance):void
	{
		emit("pause-selected", track);
	}

	function getDanceFiltersGroup(group: string): Array<DanceFilter>
	{
		switch(props.setDanceCategory)
		{
			case "Hornpipe Set Dances":
				return hornpipeSetDanceFilters.map((dance) => dance).filter((item) => item.group === group);
			case "Jig Set Dances":
				return jigSetDanceFilters.map((dance) => dance).filter((item) => item.group === group);
			default:
				return [];
		}
	}

	function loadHornpipeSetDanceFilters()
	{
		hornpipeSetDanceFilters.length = 0;
		const filters = allSetDanceFilters.default.map((filter) => filter)
			.filter((item) => item.type === "Hornpipe");
		for(const filter of filters)
		{
			hornpipeSetDanceFilters.push(new DanceFilter({
				dance: filter.dance,
				group: filter.group,
				isSelected: filter.isSelected
			}));
		}
	}

	function loadJigSetDanceFilters()
	{
		jigSetDanceFilters.length = 0;
		const filters = allSetDanceFilters.default.map((filter) => filter)
			.filter((item) => item.type === "jig");
		for(const filter of filters)
		{
			jigSetDanceFilters.push(new DanceFilter({
				dance: filter.dance,
				group: filter.group,
				isSelected: filter.isSelected
			}));
		}
	}
	
	function highlightSelectedHornpipeSetDanceName(value: string)
	{
		if (hornpipeSetDanceFilters)
		{
			for(const filter of hornpipeSetDanceFilters)
			{
				filter.isSelected = (filter.dance === value ? "site-btn-primary" : "site-btn-outline-primary")
			}
		}
	}

	function highlightSelectedJigSetDanceName(value: string)
	{
		if (jigSetDanceFilters)
		{
			for(const filter of jigSetDanceFilters)
			{
				filter.isSelected = (filter.dance === value ? "site-btn-primary" : "site-btn-outline-primary")
			}
		}
	}
	onBeforeMount(function()
	{
		loadHornpipeSetDanceFilters();
		loadJigSetDanceFilters();
		highlightSelectedHornpipeSetDanceName("Ace and Deuce");
		highlightSelectedJigSetDanceName("Blackthorn Stick");
	});

    return {
		trackSelected,
		pauseAudio,
		hornpipeSetDanceFilters,
		filterToApply,
		danceFilterToApply,
		changeFilter,
		getDanceFiltersGroup
    };
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../styles/palette";
@import "../styles/layout";

.app-company-logo {
  margin-top: pxToRem(21px);
  background: $actions-cards-2;
  text-align: center;
  border: 0px;
}

#set-dance-layout
{
	display: flex;
	flex-direction: row;
	justify-content: space-evenly;
}
#dance-filters
{
	display: flex;
	justify-content: space-evenly;
}

#dance-filters-group-one, 
#dance-filters-group-two,
#dance-filters-group-three
{
	display: flex;
	flex-direction: column;
	justify-content: left;
	align-items: flex-start;
	margin-bottom: 15px;
	max-width: 210px;
}

#track-panels
{
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	margin-bottom: 15px;
}
@media only screen and (max-width: 780px) {
	.grade-layout
	{
		max-width: 100%;
	}
	#dance-filters
	{
		flex-wrap: wrap;
	}
	#set-dance-layout
	{
		flex-wrap: wrap;
	}
}
@media only screen and (max-width: 1360px) {
	#track-panels
	{
		flex-direction: column;
		flex-wrap: wrap;
	}
}
</style>
